import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { createColumnHelper, type ColumnDef } from '@tanstack/react-table'
import { Inbox, Plus } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { discardInboxItem, inboxItemsQueryOptions, inboxKeys } from '@/features/inbox/api'
import { InboxItemFormDrawer } from '@/features/inbox/inbox-item-form-drawer'
import { InboxProcessDrawer } from '@/features/inbox/inbox-process-drawer'
import {
  CAIXA_ORDEM_DEFAULT,
  CAIXA_STATUS_DEFAULT,
  caixaDeEntradaSearchSchema,
  toInboxListParams,
  type CaixaDeEntradaSearch,
} from '@/features/inbox/search'
import { formatBusinessDate } from '@/features/schedules/components/date-text'
import { MoneyText } from '@/features/schedules/components/money-text'
import type { InboxItemOut } from '@/shared/api/types'
import { useApiErrorMessage } from '@/shared/i18n/use-api-error-message'
import { Badge } from '@/shared/ui/badge'
import { Button } from '@/shared/ui/button'
import { ConfirmDialog } from '@/shared/ui/confirm-dialog'
import { EmptyState } from '@/shared/ui/empty-state'
import { SelectNative } from '@/shared/ui/select-native'
import { PageHeader } from '@/widgets/app-shell/page-header'
import { DataTable } from '@/widgets/data-table/data-table'
import { NoResultsState } from '@/widgets/data-table/no-results-state'
import { RowEditButton } from '@/widgets/data-table/row-edit-button'
import { TablePager } from '@/widgets/data-table/table-pager'
import { fromSortingState, toSortingState } from '@/widgets/data-table/table-search'
import { FiltroCampo, TableToolbar, type FiltroChip } from '@/widgets/data-table/table-toolbar'

export const Route = createFileRoute('/_authed/caixa-de-entrada')({
  validateSearch: caixaDeEntradaSearchSchema,
  loaderDeps: ({ search }) => ({ params: toInboxListParams(search) }),
  loader: ({ context, deps }) =>
    context.queryClient.ensureQueryData(inboxItemsQueryOptions(deps.params)),
  component: CaixaDeEntradaPage,
})

const col = createColumnHelper<InboxItemOut>()

const STATUS_TONE = { pending: 'info', processed: 'positive', discarded: 'neutral' } as const

function CaixaDeEntradaPage() {
  const { t, i18n } = useTranslation(['inbox', 'nav', 'common'])
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const qc = useQueryClient()
  const mensagemDeErro = useApiErrorMessage()

  const [criarAberto, setCriarAberto] = useState(false)
  const [emEdicao, setEmEdicao] = useState<InboxItemOut | null>(null)
  const [aProcessar, setAProcessar] = useState<InboxItemOut | null>(null)
  const [aDescartar, setADescartar] = useState<InboxItemOut | null>(null)

  const { data, isLoading, isFetching, error, refetch } = useQuery(
    inboxItemsQueryOptions(toInboxListParams(search)),
  )

  const aplicar = (patch: Partial<CaixaDeEntradaSearch>) => {
    // replace: filtrar não é navegação — empilhar cada tecla da busca faria o
    // "voltar" do navegador desfazer letra por letra.
    //
    // `to` EXPLÍCITO + `prev` ANOTADO: sem os dois o destino fica ambíguo, o
    // updater cai em `any` (TS7006) e a navegação pode virar no-op silencioso.
    void navigate({
      to: '/caixa-de-entrada',
      search: (prev: CaixaDeEntradaSearch) => ({ ...prev, ...patch }),
      replace: true,
    })
  }
  /** Filtrar/ordenar volta para a página 1 — senão abre num offset vazio. */
  const filtrar = (patch: Partial<CaixaDeEntradaSearch>) => {
    aplicar({ ...patch, pagina: 1 })
  }
  const limparFiltros = () => {
    // `status` volta ao default da triagem, não a "ausente": sem status a tela
    // misturaria pendentes, processados e descartados numa lista só.
    filtrar({ q: undefined, status: CAIXA_STATUS_DEFAULT })
  }

  const chips: FiltroChip[] = []
  if (search.q !== undefined) {
    chips.push({
      id: 'q',
      label: `${t('inbox:filters.searchLabel')}: ${search.q}`,
      onRemove: () => {
        filtrar({ q: undefined })
      },
    })
  }
  if (search.status !== CAIXA_STATUS_DEFAULT) {
    chips.push({
      id: 'status',
      label: `${t('inbox:filters.status')}: ${t(`inbox:status.${search.status}`)}`,
      onRemove: () => {
        filtrar({ status: CAIXA_STATUS_DEFAULT })
      },
    })
  }

  /**
   * O chip de status NÃO conta como "filtro que não achou nada".
   *
   * Ele escolhe QUAL fila da triagem está aberta, e cada uma tem seu próprio
   * vazio verdadeiro ("Nenhum item processado"), sem CTA de cadastro. Trocá-lo
   * por "Nenhum resultado / limpar filtros" perderia a explicação e ofereceria
   * a ação errada. Já uma BUSCA sem resultado torna aquele texto mentira — é
   * aí que o NoResultsState entra.
   */
  const chipsDeBusca = chips.filter((c) => c.id !== 'status')

  const columns = useMemo(
    () =>
      [
        col.accessor('description', {
          id: 'description',
          header: t('inbox:columns.description'),
          // A allowlist de order_by do backend é created_at/due_date/amount.
          // Sem este `false` explícito o react-table renderiza o header como
          // botão com seta — clicável e sem efeito nenhum.
          enableSorting: false,
        }),
        col.accessor((r) => r.kind, {
          id: 'kind',
          header: t('inbox:columns.kind'),
          cell: (c) => {
            const k = c.getValue()
            return k === null ? '—' : t(`inbox:kinds.${k}`)
          },
          enableSorting: false,
        }),
        col.accessor((r) => r.due_date, {
          id: 'due_date',
          header: t('inbox:columns.dueDate'),
          cell: (c) => {
            const d = c.getValue()
            return d === null ? '—' : formatBusinessDate(d, i18n.language)
          },
          enableSorting: true,
        }),
        col.accessor((r) => r.amount, {
          id: 'amount',
          header: t('inbox:columns.amount'),
          cell: (c) => <MoneyText value={c.getValue()} />,
          enableSorting: true,
        }),
        col.accessor('status', {
          id: 'status',
          header: t('inbox:columns.status'),
          cell: (c) => (
            <Badge tone={STATUS_TONE[c.getValue()]}>{t(`inbox:status.${c.getValue()}`)}</Badge>
          ),
          enableSorting: false,
        }),
        col.display({
          id: 'actions',
          header: () => null,
          cell: (c) => {
            const it = c.row.original
            // Só `pending` aceita ação — processed/discarded são terminais
            // (o backend responde 409 inbox_item_not_pending).
            if (it.status !== 'pending') return null
            return (
              <div className="flex items-center justify-end gap-1">
                <RowEditButton
                  ariaLabel={t('inbox:editAria', { description: it.description })}
                  onEdit={() => {
                    setEmEdicao(it)
                  }}
                />
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={() => {
                    setADescartar(it)
                  }}
                >
                  {t('inbox:actions.discard')}
                </Button>
                <Button
                  variant="outline"
                  size="xs"
                  onClick={() => {
                    setAProcessar(it)
                  }}
                >
                  {t('inbox:actions.process')}
                </Button>
              </div>
            )
          },
        }),
      ] as ColumnDef<InboxItemOut>[],
    [t, i18n.language],
  )

  const descartar = useMutation({
    mutationFn: (id: string) => discardInboxItem(id),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: inboxKeys.lists() })
      toast.success(t('inbox:discarded'))
      setADescartar(null)
    },
    onError: (err: unknown) => {
      toast.error(mensagemDeErro(err))
      setADescartar(null)
    },
  })

  const botaoNovo = (
    <Button
      variant="primary"
      onClick={() => {
        setCriarAberto(true)
      }}
    >
      <Plus aria-hidden />
      {t('inbox:new')}
    </Button>
  )

  return (
    <>
      <PageHeader title={t('nav:inbox')} actions={botaoNovo} />

      <div className="flex flex-col gap-4 p-6">
        <TableToolbar
          inline
          busca={search.q}
          onBuscaChange={(q) => {
            filtrar({ q })
          }}
          buscaPlaceholder={t('inbox:filters.searchPlaceholder')}
          chips={chips}
          onLimpar={limparFiltros}
        >
          <FiltroCampo id="filtro-status" label={t('inbox:filters.status')} className="w-44">
            <SelectNative
              id="filtro-status"
              value={search.status}
              onChange={(e) => {
                filtrar({ status: e.target.value as CaixaDeEntradaSearch['status'] })
              }}
            >
              <option value="pending">{t('inbox:status.pending')}</option>
              <option value="processed">{t('inbox:status.processed')}</option>
              <option value="discarded">{t('inbox:status.discarded')}</option>
            </SelectNative>
          </FiltroCampo>
        </TableToolbar>

        <DataTable
          columns={columns}
          page={data}
          isLoading={isLoading}
          isFetching={isFetching}
          error={error}
          onRetry={() => void refetch()}
          getRowId={(r) => r.id}
          sorting={toSortingState(search.ordenar)}
          onSortingChange={(s) => {
            filtrar({ ordenar: fromSortingState(s, CAIXA_ORDEM_DEFAULT) })
          }}
          footer={
            <TablePager
              total={data?.total ?? 0}
              pagina={search.pagina}
              linhas={search.linhas}
              onPageChange={(pagina) => {
                aplicar({ pagina })
              }}
              onLinhasChange={(linhas) => {
                aplicar({ linhas, pagina: 1 })
              }}
            />
          }
          ariaLabel={t('inbox:ariaTable')}
          empty={
            chipsDeBusca.length > 0 ? (
              <NoResultsState onLimpar={limparFiltros} />
            ) : (
              <EmptyState
                icon={Inbox}
                title={t(`inbox:empty.${search.status}.title`)}
                description={t(`inbox:empty.${search.status}.description`)}
                {...(search.status === 'pending' && { action: botaoNovo })}
              />
            )
          }
        />
      </div>

      <InboxItemFormDrawer
        key={criarAberto ? 'novo' : 'fechado'}
        open={criarAberto}
        onOpenChange={setCriarAberto}
      />

      {emEdicao !== null && (
        <InboxItemFormDrawer
          key={emEdicao.id}
          item={emEdicao}
          open
          onOpenChange={(aberto) => {
            if (!aberto) setEmEdicao(null)
          }}
        />
      )}

      <InboxProcessDrawer
        // key: remonta o form por item (defaultValues herdam o rascunho) e
        // renova a intenção de idempotência.
        key={aProcessar?.id ?? 'nenhum'}
        item={aProcessar}
        onOpenChange={(aberto) => {
          if (!aberto) setAProcessar(null)
        }}
      />

      <ConfirmDialog
        open={aDescartar !== null}
        onOpenChange={(aberto) => {
          if (!aberto) setADescartar(null)
        }}
        title={t('inbox:discardConfirm.title', { description: aDescartar?.description ?? '' })}
        description={t('inbox:discardConfirm.description')}
        confirmLabel={t('inbox:actions.discard')}
        destructive
        loading={descartar.isPending}
        onConfirm={() => {
          if (aDescartar !== null) descartar.mutate(aDescartar.id)
        }}
      />
    </>
  )
}
